import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  Loader2,
  FileText,
  Lock,
  LockOpen,
  Calendar,
  Search,
  XCircle,
  ChevronRight,
  Scale,
  ExternalLink,
  HelpCircle,
  Building2,
  Users,
  Upload,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { useAuthStore } from '@/store/auth.store';
import { vaultService } from '@/services/vault.service';
import { useVaultKeyStore } from '@/store/vaultKeyStore';
import { Vault, VaultEntityType, VaultEncryptionMode, VaultStatus } from '@/types/vault.types';
import { UnlockVaultModal, TwoFactorRequiredPrompt, VaultUserLayout, VaultUploadModal } from '../components';

/**
 * VaultUserDashboard
 *
 * Simplified dashboard for users with the vault_user role.
 * Shows only their vaults without access to the main dashboard features.
 */
export const VaultUserDashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [selectedVaultId, setSelectedVaultId] = useState<string | null>(null);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [requires2fa] = useState(false);
  const [activeTab, setActiveTab] = useState<'folder' | 'employee'>('folder');
  const [uploadVault, setUploadVault] = useState<{ id: string; name: string } | null>(null);

  // Fetch user's vaults
  const {
    data: vaultsResponse,
    isLoading: vaultsLoading,
    error: vaultsError,
  } = useQuery({
    queryKey: ['my-vaults'],
    queryFn: () => vaultService.getMyVaults(),
  });

  const vaults = vaultsResponse?.data || [];

  // Vault key management - Use unlockedVaults directly for proper re-renders
  const unlockedVaults = useVaultKeyStore((state) => state.unlockedVaults);
  const lockVault = useVaultKeyStore((state) => state.lockVault);
  const isVaultUnlocked = (vaultId: string) => unlockedVaults.has(vaultId);

  // Check for 2FA requirement error
  const vaultsErrorData = (
    vaultsError as AxiosError<{
      error?: string;
      twoFactorRequired?: boolean;
    }> | null
  )?.response?.data;
  const is2faRequired =
    vaultsErrorData?.error === 'TWO_FACTOR_REQUIRED' ||
    vaultsErrorData?.twoFactorRequired === true;

  const handleUnlockVault = (vaultId: string) => {
    setSelectedVaultId(vaultId);
    setShowUnlockModal(true);
  };

  const handleLockVault = (vaultId: string) => {
    lockVault(vaultId);
    toast.success(t('vault.messages.locked', 'Coffre-fort verrouillé'));
  };

  const handleViewVault = (vault: Vault) => {
    // SERVER_SIDE vaults don't need unlocking
    if (vault.encryptionMode === VaultEncryptionMode.SERVER_SIDE) {
      navigate(`/vault/${vault.uuid}`);
      return;
    }
    if (!isVaultUnlocked(vault.uuid)) {
      handleUnlockVault(vault.uuid);
    } else {
      navigate(`/vault/${vault.uuid}`);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(
      i18n.language === 'fr' ? 'fr-FR' : 'en-US',
      {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }
    );
  };

  const getStatusBadge = (status: VaultStatus) => {
    switch (status) {
      case VaultStatus.ACTIVE:
        return (
          <span className="inline-flex items-center rounded-full bg-success-50 px-2 py-0.5 text-xs font-medium text-success-700">
            {t('vault.status.active', 'Actif')}
          </span>
        );
      case VaultStatus.PENDING_USER_ACTIVATION:
        return (
          <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
            {t('vault.status.pendingActivation', 'En attente')}
          </span>
        );
      case VaultStatus.SUSPENDED:
        return (
          <span className="inline-flex items-center rounded-full bg-error-50 px-2 py-0.5 text-xs font-medium text-error-700">
            {t('vault.status.suspended', 'Suspendu')}
          </span>
        );
      default:
        return null;
    }
  };

  // Filter vaults by tab and search query
  const folderVaults = vaults.filter(
    (v) => v.entityType === VaultEntityType.FOLDER || !v.entityType
  );
  const employeeVaults = vaults.filter(
    (v) => v.entityType === VaultEntityType.EMPLOYEE
  );

  // Show tabs only if user has at least one folder vault
  const showTabs = folderVaults.length > 0;

  const currentVaults = showTabs
    ? activeTab === 'folder'
      ? folderVaults
      : employeeVaults
    : vaults;

  const filteredVaults = currentVaults.filter((vault) =>
    vault.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Loading state
  if (vaultsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-4">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary-600" />
          <p className="text-sm text-neutral-600">
            {t('vault.myVaults.loading', 'Chargement de vos coffres-forts...')}
          </p>
        </div>
      </div>
    );
  }

  // 2FA required state
  if (requires2fa || is2faRequired) {
    return (
      <VaultUserLayout
        title={t('vault.myVaults.title', 'Mes coffres-forts')}
      >
        <div className="flex flex-1 items-center justify-center">
          <TwoFactorRequiredPrompt
            onSetup={() => navigate('/settings?tab=security')}
          />
        </div>
      </VaultUserLayout>
    );
  }

  return (
    <VaultUserLayout
      title={t('vault.myVaults.title', 'Mes coffres-forts')}
      subtitle={t('vault.myVaults.welcome', 'Bienvenue, {{name}}', {
        name: user?.firstName || user?.email,
      })}
      showSettings
    >
      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50">
              <Shield className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-neutral-900">{vaults.length}</p>
              <p className="text-xs text-neutral-500">
                {t('vault.myVaults.stats.total', 'Coffres-forts')}
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-success-50">
              <LockOpen className="h-5 w-5 text-success-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-neutral-900">
                {vaults.filter((v) => v.encryptionMode === VaultEncryptionMode.SERVER_SIDE || isVaultUnlocked(v.uuid)).length}
              </p>
              <p className="text-xs text-neutral-500">
                {t('vault.myVaults.stats.accessible', 'Accessibles')}
              </p>
            </div>
          </div>
        </div>
        <div className="col-span-2 rounded-lg border border-neutral-200 bg-white p-4 sm:col-span-1">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50">
              <Lock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-neutral-900">
                {vaults.filter((v) => v.encryptionMode !== VaultEncryptionMode.SERVER_SIDE && !isVaultUnlocked(v.uuid)).length}
              </p>
              <p className="text-xs text-neutral-500">
                {t('vault.myVaults.stats.locked', 'Verrouillés')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      {showTabs && <div className="mb-4 flex gap-1 rounded-lg border border-neutral-200 bg-neutral-100 p-1">
        <button
          onClick={() => setActiveTab('folder')}
          className={`flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'folder'
              ? 'bg-white text-neutral-900 shadow-sm'
              : 'text-neutral-500 hover:text-neutral-700'
          }`}
        >
          <Building2 className="h-4 w-4" />
          {t('vault.myVaults.tabs.companies', 'Entreprises')}
          <span className={`rounded-full px-1.5 py-0.5 text-xs font-medium ${
            activeTab === 'folder'
              ? 'bg-primary-100 text-primary-700'
              : 'bg-neutral-200 text-neutral-500'
          }`}>
            {folderVaults.length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('employee')}
          className={`flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'employee'
              ? 'bg-white text-neutral-900 shadow-sm'
              : 'text-neutral-500 hover:text-neutral-700'
          }`}
        >
          <Users className="h-4 w-4" />
          {t('vault.myVaults.tabs.employees', 'Employés')}
          <span className={`rounded-full px-1.5 py-0.5 text-xs font-medium ${
            activeTab === 'employee'
              ? 'bg-primary-100 text-primary-700'
              : 'bg-neutral-200 text-neutral-500'
          }`}>
            {employeeVaults.length}
          </span>
        </button>
      </div>}

      {/* Vaults List */}
      <div className="rounded-xl border border-neutral-200 bg-white">
        {/* Search */}
        <div className="border-b border-neutral-200 p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              placeholder={t('vault.myVaults.searchPlaceholder', 'Rechercher un coffre-fort...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 py-2 pl-9 pr-4 text-sm text-neutral-900 placeholder-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Vaults */}
        <div className="divide-y divide-neutral-100">
          {filteredVaults.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100">
                {searchQuery ? (
                  <Search className="h-6 w-6 text-neutral-400" />
                ) : (
                  <XCircle className="h-6 w-6 text-neutral-400" />
                )}
              </div>
              <h3 className="mb-2 text-base font-medium text-neutral-900">
                {searchQuery
                  ? t('vault.myVaults.noResults', 'Aucun résultat')
                  : t('vault.myVaults.empty', 'Aucun coffre-fort')}
              </h3>
              <p className="text-sm text-neutral-500">
                {searchQuery
                  ? t('vault.myVaults.noResultsDescription', 'Essayez une autre recherche')
                  : t(
                      'vault.myVaults.emptyDescription',
                      "Vous n'avez pas encore de coffre-fort"
                    )}
              </p>
            </div>
          ) : (
            filteredVaults.map((vault) => (
              <div
                key={vault.uuid}
                className="flex items-center justify-between p-4 transition-colors hover:bg-neutral-50"
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`flex h-11 w-11 items-center justify-center rounded-lg ${
                      vault.encryptionMode === VaultEncryptionMode.SERVER_SIDE || isVaultUnlocked(vault.uuid)
                        ? 'bg-success-50'
                        : 'bg-neutral-100'
                    }`}
                  >
                    {vault.encryptionMode === VaultEncryptionMode.SERVER_SIDE || isVaultUnlocked(vault.uuid) ? (
                      <LockOpen className="h-5 w-5 text-success-600" />
                    ) : (
                      <Lock className="h-5 w-5 text-neutral-500" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-neutral-900">{vault.name}</h3>
                      {getStatusBadge(vault.status)}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-neutral-500">
                      <Calendar className="h-3 w-3" />
                      <span>
                        {t('vault.myVaults.created', 'Créé le {{date}}', {
                          date: formatDate(vault.createdAt),
                        })}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {vault.status === VaultStatus.PENDING_USER_ACTIVATION ? (
                    vault.accessType === 'via_folder' ? (
                      /* Employeur : ne peut pas initialiser le vault d'un employé */
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700">
                        <Lock className="h-3.5 w-3.5" />
                        {t('vault.myVaults.pendingEmployee', "En attente d'activation par l'employé")}
                      </span>
                    ) : (
                      <button
                        onClick={() => navigate(`/vault/initialize/${vault.uuid}`)}
                        className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
                      >
                        {t('vault.myVaults.actions.initialize', 'Initialiser')}
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    )
                  ) : vault.encryptionMode === VaultEncryptionMode.SERVER_SIDE ? (
                    /* SERVER_SIDE vaults: no unlock needed, direct access */
                    vault.status === VaultStatus.ACTIVE ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() =>
                            setUploadVault({ id: vault.uuid, name: vault.name })
                          }
                          className="flex items-center gap-1 rounded-lg border border-purple-300 bg-white px-3 py-1.5 text-xs font-medium text-purple-700 transition-colors hover:bg-purple-50"
                        >
                          <Upload className="h-3.5 w-3.5" />
                          {t('vault.myVaults.actions.upload', 'Deposer')}
                        </button>
                        <button
                          onClick={() => handleViewVault(vault)}
                          className="flex items-center gap-1 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-primary-700"
                        >
                          <FileText className="h-3.5 w-3.5" />
                          {t('vault.myVaults.actions.view', 'Voir')}
                        </button>
                      </div>
                    ) : null
                  ) : vault.accessType === 'via_folder' ? (
                    /* Vault accessible via folder (employeur) : bouton Deposer uniquement */
                    vault.status === VaultStatus.ACTIVE ? (
                      <button
                        onClick={() =>
                          setUploadVault({ id: vault.uuid, name: vault.name })
                        }
                        className="flex items-center gap-1 rounded-lg border border-purple-300 bg-white px-3 py-1.5 text-xs font-medium text-purple-700 transition-colors hover:bg-purple-50"
                      >
                        <Upload className="h-3.5 w-3.5" />
                        {t('vault.myVaults.actions.upload', 'Deposer')}
                      </button>
                    ) : null
                  ) : (
                    /* Accès direct (permission) : unlock/lock/view standard (ZERO_KNOWLEDGE only) */
                    <>
                      {isVaultUnlocked(vault.uuid) ? (
                        <>
                          <button
                            onClick={() => handleLockVault(vault.uuid)}
                            className="flex items-center gap-1 rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
                          >
                            <Lock className="h-3.5 w-3.5" />
                            {t('vault.myVaults.actions.lock', 'Verrouiller')}
                          </button>
                          <button
                            onClick={() => handleViewVault(vault)}
                            className="flex items-center gap-1 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-primary-700"
                          >
                            <FileText className="h-3.5 w-3.5" />
                            {t('vault.myVaults.actions.view', 'Voir')}
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleUnlockVault(vault.uuid)}
                          className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
                        >
                          <LockOpen className="h-4 w-4" />
                          {t('vault.myVaults.actions.unlock', 'Deverrouiller')}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Security Note */}
      <div className="mt-6 rounded-xl border border-neutral-200 bg-white px-6 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 text-xs text-neutral-600">
            <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-neutral-400" />
            <p>
              {t(
                'vault.myVaults.securityNote',
                'Vos documents sont protégés par chiffrement de bout en bout. Seul vous pouvez les déverrouiller avec votre mot de passe.'
              )}
            </p>
          </div>
          <button
            onClick={() => navigate('/my-vaults/compliance')}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-success-50 px-3 py-1 text-xs font-medium text-success-700 transition-colors hover:bg-success-100"
          >
            <Scale className="h-3.5 w-3.5" />
            {t('vault.compliance.badge', 'Conforme')}
          </button>
        </div>
      </div>

      {/* Compliance Info */}
      <div className="mt-4 rounded-xl border border-primary-200 bg-gradient-to-br from-primary-50 to-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
              <Scale className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-900">
                {t('vault.myVaults.complianceTitle', 'Cadre légal et mesures du coffre-fort')}
              </p>
              <p className="text-xs text-neutral-500">
                {t('vault.myVaults.complianceSubtitle', 'Loi El Khomri, RGPD, eIDAS')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/my-vaults/help')}
              className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-50"
            >
              <HelpCircle className="h-4 w-4" />
              {t('vault.myVaults.help', 'Aide / FAQ')}
            </button>
            <button
              onClick={() => navigate('/my-vaults/compliance')}
              className="flex items-center gap-2 rounded-lg border border-primary-200 bg-white px-3 py-2 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50"
            >
              {t('vault.myVaults.viewCompliance', 'Voir la documentation')}
              <ExternalLink className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Unlock Modal */}
      {selectedVaultId && (
        <UnlockVaultModal
          isOpen={showUnlockModal}
          onClose={() => {
            setShowUnlockModal(false);
            setSelectedVaultId(null);
          }}
          vaultId={selectedVaultId}
        />
      )}

      {/* Upload Modal */}
      {uploadVault && (
        <VaultUploadModal
          isOpen={!!uploadVault}
          onClose={() => setUploadVault(null)}
          vaultId={uploadVault.id}
          vaultName={uploadVault.name}
        />
      )}
    </VaultUserLayout>
  );
};
